import React, { useState, useEffect } from 'react';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

export type LoadingVariant = 'fullscreen' | 'overlay' | 'inline' | 'page';
export type LoadingAnimation = 'pulse' | 'breathing' | 'bounce' | 'spin' | 'fade';

interface LoadingProps {
  /**
   * Variant tampilan loading
   * - fullscreen: Menutupi seluruh layar (untuk initial app load)
   * - overlay: Menutupi konten dengan backdrop (untuk action/submit)
   * - page: Di tengah area konten (untuk fetch data halaman)
   * - inline: Di dalam container tertentu (untuk bagian kecil)
   */
  variant?: LoadingVariant;

  /**
   * Jenis animasi
   */
  animation?: LoadingAnimation;

  /**
   * Ukuran logo
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Teks loading (opsional)
   */
  message?: string;

  /**
   * Warna background (untuk fullscreen/overlay)
   */
  backdrop?: 'light' | 'dark' | 'blur' | 'none';

  /**
   * Custom className
   */
  className?: string;

  /**
   * Force show logo meskipun belum hydrated
   */
  forceShowLogo?: boolean;
}

const Loading: React.FC<LoadingProps> = ({
  variant = 'inline',
  animation = 'breathing',
  size = 'md',
  message,
  backdrop = 'blur',
  className = '',
  forceShowLogo = false,
}) => {
  const { settings, isHydrated } = useSystemSettingsStore();
  const [logoError, setLogoError] = useState(false);

  const logoUrl = getAssetUrl(settings.logo_url);
  const appName = settings.app_name || 'SIM-EVAL';

  // Brand initials sebagai fallback
  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Tentukan apakah pakai logo atau fallback
  const showLogo = (isHydrated || forceShowLogo) && logoUrl && !logoError;

  // Reset error saat logo URL berubah
  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  // Size mapping
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-20 h-20',
    lg: 'w-32 h-32',
    xl: 'w-48 h-48',
  };

  // Animation classes
  const animationClasses = {
    pulse: 'animate-pulse',
    breathing: 'animate-breathing',
    bounce: 'animate-bounce',
    spin: 'animate-spin',
    fade: 'animate-fade',
  };

  // Backdrop classes
  const backdropClasses = {
    light: 'bg-white/90',
    dark: 'bg-neutral-900/90',
    blur: 'bg-white/70 backdrop-blur-md',
    none: '',
  };

  // Container berdasarkan variant
  const containerClasses = {
    fullscreen: `fixed inset-0 z-[9999] flex flex-col items-center justify-center ${backdropClasses[backdrop]}`,
    overlay: `fixed inset-0 z-50 flex flex-col items-center justify-center ${backdropClasses[backdrop]}`,
    page: `flex flex-col items-center justify-center min-h-[400px] py-12`,
    inline: `flex flex-col items-center justify-center py-8`,
  };

  return (
    <div className={`${containerClasses[variant]} ${className}`}>
      {/* Logo Container */}
      <div className={`relative ${sizeClasses[size]}`}>
        {showLogo ? (
          // Logo Image dengan animasi
          <div className={`w-full h-full ${animationClasses[animation]}`}>
            <img
              src={logoUrl!}
              alt={`Loading ${appName}`}
              className="w-full h-full object-contain"
              onError={() => setLogoError(true)}
            />
          </div>
        ) : (
          // Fallback: Initials dengan gradient background
          <div
            className={`
              w-full h-full rounded-2xl
              bg-gradient-to-br from-teal-main to-teal-dark
              flex items-center justify-center
              text-white font-black
              ${animationClasses[animation]}
            `}
            style={{
              fontSize: size === 'sm' ? '1rem' : size === 'md' ? '1.5rem' : size === 'lg' ? '2.5rem' : '4rem',
            }}
          >
            {brandInitials}
          </div>
        )}

        {/* Spinner overlay (opsional, hanya untuk variant tertentu) */}
        {animation === 'spin' && variant === 'fullscreen' && (
          <div className="absolute inset-0 rounded-full border-4 border-teal-main/20 border-t-teal-main animate-spin" />
        )}
      </div>

      {/* Loading Message */}
      {message && (
        <p
          className={`
            mt-4 font-medium text-center
            ${variant === 'fullscreen' || variant === 'overlay' ? 'text-neutral-text-primary' : 'text-neutral-text-secondary'}
            ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}
          `}
        >
          {message}
        </p>
      )}

      {/* Animated dots (untuk fullscreen/overlay) */}
      {(variant === 'fullscreen' || variant === 'overlay') && !message && (
        <div className="mt-4 flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-teal-main rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 bg-teal-main rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 bg-teal-main rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      )}
    </div>
  );
};

export default Loading;
